/* tslint:disable:no-string-literal */
import {ChangeDetectionStrategy, Component, ElementRef, Input, OnInit, ViewChild} from '@angular/core';
import {BlocksComponent} from '@blocks/blocks.component';
import {Size} from '@paperlessio/sdk/api/models'
import {BlockMutationService} from '@blocks/services/block-mutation/block-mutation.service';
import {Block, PdfPart} from '@paperlessio/sdk/api/models';
import {take} from 'rxjs/operators';
import {RouterStore} from '@paperlessio/sdk/util';

@Component({
  selector: 'pl-pdf-part',
  templateUrl: './pdf-part.component.html',
  styleUrls: ['./pdf-part.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PdfPartComponent extends BlocksComponent implements OnInit {
  private readonly desiredWidth = 1140;

  public selectedBlockId: number;
  public scaleFactor = 1;

  public wrapperWidth: number;
  public wrapperHeight: number;

  public dropZoneWidth: number;
  public dropZoneHeight: number;

  public pageWidth: number;
  public imageElementWidth: number;

  public imageWidth: number;
  public imageHeight: number;
  public aspectRatio: number;

  get refImageHeight(): number {
    return this.block.height;
  }

  get refImageWidth(): number {
    return this.block.width;
  }

  @Input() block: PdfPart;

  @ViewChild('imageElement') imageElement: ElementRef<HTMLImageElement>;

  get previewPngUrl(): string {
    return this.block?.file?.variants?.default_url ?? '';
  }

  get preview2xPngUrl(): string {
    return this.block?.file?.variants?.default_2x_url ?? '';
  }

  get previewWebpUrl(): string {
    return this.block?.file?.variants?.default_webp_url ?? '';
  }

  get preview2xWebpUrl(): string {
    return this.block?.file?.variants?.default_2x_webp_url ?? '';
  }

  get srcsetPng(): string {
    return `${this.previewPngUrl} 1x, ${this.preview2xPngUrl} 2x`;
  }

  get srcsetWebp(): string {
    return `${this.previewWebpUrl} 1x, ${this.preview2xWebpUrl} 2x`;
  }

  constructor(private blockMutationService: BlockMutationService, private routerStore: RouterStore) {
    super();
  }

  ngOnInit() {
    super.ngOnInit();

    this.subs.add(this.routerStore.routeParams.subscribe(params => {
      this.selectedBlockId = +params.block;
      this.changeDetectorRef.markForCheck();
    }));

    try {
      const resizeObserver = new ResizeObserver(entries => {
        const pageEntry = entries.find(e => e.target.matches('app-page'));
        const refImageEntry = entries.find(e => e.target.matches('.reference-img'));

        if (pageEntry) {
          this.pageWidth = this.widthFromResizeObserverEntry(pageEntry);
        }

        if (refImageEntry) {
          this.imageElementWidth = this.widthFromResizeObserverEntry(refImageEntry);
          this.imageWidth = (refImageEntry.target as HTMLImageElement)?.width || 1;
          this.imageHeight = (refImageEntry.target as HTMLImageElement)?.height || 1;

          this.dropZoneWidth = this.imageWidth;
          this.dropZoneHeight = this.imageHeight;
        }

        this.aspectRatio = this.imageWidth / this.imageHeight;

        // If image > page, scale it down
        if (this.imageElementWidth > this.pageWidth) {
          this.scaleFactor = this.pageWidth / this.imageElementWidth;
        } else {
          this.scaleFactor = 1;
        }

        // When we are in render, set the scaleFactor to 1 so the pdf parts will always be the right size according to the metadata from the backend
        if (this.elementRef.nativeElement.closest('app-document-render') != null) {
          this.scaleFactor = 1;
        }

        this.wrapperWidth = this.imageWidth * this.scaleFactor;

        // TODO: Very crazy fix to prevent diverging pdf overlays
        this.wrapperHeight = (this.imageHeight * this.scaleFactor) - 1;

        // Detect changes is used here because markForCheck was buggy in render.
        this.changeDetectorRef.detectChanges();
      });

      const closestPage = this.elementRef.nativeElement.closest('app-page');
      if (closestPage) {
        resizeObserver.observe(closestPage);
      }

      resizeObserver.observe(this.elementRef.nativeElement.querySelector('.reference-img'));
    } catch (e) {
      // The ResizeObserver throws a lot of unnecessary errors we really can't do anything about.
    }
  }

  childResized(size: Size, child: Block) {
    child.settings.absoluteSize = size;
    this.blockMutationService.updateBlock(child).pipe(take(1)).subscribe();
  }

  widthFromResizeObserverEntry(entry: ResizeObserverEntry) {
    if (entry.borderBoxSize) {
      return entry.borderBoxSize[0].inlineSize;
    } else if (entry.contentBoxSize) {
      return entry.contentBoxSize[0].inlineSize;
    } else if (entry.contentRect) {
      return entry.contentRect.width;
    } else {
      return this.desiredWidth;
    }
  }
}
